import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Upload, Image as ImageIcon, Download, Eye, EyeOff } from 'lucide-react';
import { supabase, supabaseConfigured } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { isSiteAdmin } from '../../../utils/isSiteAdmin';
import {
  loadCatalogAdminRows,
  type AutomobileBrandRow,
  type AutomobileModelRow,
} from '../../../utils/automobileCatalogAdmin';
import {
  exportOverridesJson,
  importOverridesJson,
  getLocalModelYears,
  setLocalBrandLogo,
  setLocalModelImage,
  setLocalModelYears,
} from '../../../utils/automobileLocalOverrides';
import {
  AUTOMOBILE_BODY_CHIP,
  AUTOMOBILE_BODY_TYPE_LABELS,
  AUTOMOBILE_BODY_TYPE_ORDER,
  AUTOMOBILE_COMPANY_STATUS_LABELS,
  type AutomobileBodyType,
  type AutomobileCompanyStatus,
  type AutomobileVerificationLevel,
} from '../../../types/automobile';
import {
  AUTOMOBILE_COUNTRY_OPTIONS,
  formatAutomobileCountry,
} from '../../../utils/automobileCountry';
import {
  formatProductionYearsForInput,
  parseProductionYearsInput,
  productionYearBounds,
} from '../../../utils/automobileProductionYears';
import { freshImageUrl, isUserAutomobileImageUrl } from '../../../utils/automobileImageUrl';
import {
  formatFileSize,
  getImageFileDimensions,
  resizeImageFile,
  type ImageDimensions,
} from '../../../utils/resizeImageFile';
import { getAutomobileModelStableKey, normalizeAutomobileModelDisplayName } from '../../../utils/automobileModelDisplay';
import {
  getAutomobileCatalogBase,
  getAutomobileCatalogBrandMetadata,
} from '../../../utils/automobileCatalog';
import {
  countPublishedAutomobileModels,
  type SupabaseBrandRow,
  type SupabaseModelRow,
} from '../../../utils/automobileCatalogMerge';
import { refreshAutomobileCatalogFromSupabase } from '../../../utils/automobileCatalogStore';
import AutomobileModelImage from '../AutomobileModelImage';
import AutomobileBrandLogoImage from '../AutomobileBrandLogoImage';
import AutomobileModelImageFrameEditor from './AutomobileModelImageFrameEditor';
import AutomobileLogoFrameEditor from './AutomobileLogoFrameEditor';
import {
  DEFAULT_MODEL_IMAGE_FRAME,
  logoFrameFromRow,
  logoFrameToPayload,
  modelImageFrameFromRow,
  modelImageFrameToPayload,
  type AutomobileModelImageFrame,
} from '../../../utils/automobileModelImageFrame';

/** Admin önizleme / tablo: görseli sabit kutuya sığdırır */
function AdminImageBox({
  src,
  alt,
  className = '',
  boxClassName,
  fit = 'contain',
  onError,
}: {
  src: string;
  alt: string;
  className?: string;
  boxClassName: string;
  fit?: 'contain' | 'cover';
  onError?: () => void;
}) {
  return (
    <div className={`relative overflow-hidden ${boxClassName}`}>
      <img
        key={src}
        src={src}
        alt={alt}
        className={`absolute inset-0 w-full h-full ${
          fit === 'cover' ? 'object-cover' : 'object-contain'
        } ${className}`}
        onError={onError}
      />
    </div>
  );
}

type Tab = 'brands' | 'models';
type DataSource = 'catalog' | 'supabase';

const MODEL_PAGE_SIZES = [50, 100, 200] as const;
const DEFAULT_MODEL_PAGE_SIZE = 50;
const BRAND_PAGE_SIZES = [25, 50, 100] as const;
const DEFAULT_BRAND_PAGE_SIZE = 50;

function AdminListPagination({
  page,
  pageCount,
  pageSize,
  pageSizes,
  total,
  itemLabel,
  onPageChange,
  onPageSizeChange,
  inputClass,
  btnGhostClass,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  pageSizes: readonly number[];
  total: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  inputClass: string;
  btnGhostClass: string;
}) {
  if (total <= 0) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/95 dark:bg-gray-900/80">
      <p className="text-xs text-gray-600 dark:text-gray-400">
        <span className="font-medium text-gray-800 dark:text-gray-200">
          {start}–{end}
        </span>{' '}
        / {total.toLocaleString('tr-TR')} {itemLabel}
        {pageCount > 1 && (
          <span className="text-gray-500">
            {' '}
            · sayfa {page}/{pageCount}
          </span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          Sayfa başına
          <select
            className={`${inputClass} !w-auto !py-1.5 !min-w-[4.5rem] text-xs`}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={btnGhostClass}
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Önceki
        </button>
        <button
          type="button"
          className={btnGhostClass}
          disabled={page >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        >
          Sonraki
        </button>
      </div>
    </div>
  );
}

/** Supabase yapılandırıldıysa varsayılan kaynak veritabanıdır (.env ile kapat: false) */
const USE_SUPABASE =
  supabaseConfigured && import.meta.env.VITE_AUTOMOBILE_ADMIN_SUPABASE !== 'false';

function buildSiteCatalogModelCount(
  brandRows: AutomobileBrandRow[],
  modelRows: AutomobileModelRow[],
): number {
  return countPublishedAutomobileModels(
    brandRows as SupabaseBrandRow[],
    modelRows as unknown as SupabaseModelRow[],
    getAutomobileCatalogBrandMetadata(),
  );
}

async function syncPublicAutomobileCatalog(): Promise<void> {
  try {
    await refreshAutomobileCatalogFromSupabase();
  } catch (err) {
    console.warn('[otomobil admin] Site kataloğu yenilenemedi:', err);
  }
}

/** Textarea içinde Ctrl+Enter / Cmd+Enter ile kaydet */
function textareaSaveKeyDown(e: React.KeyboardEvent, save: () => void | Promise<void>) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault();
    void save();
  }
}

function supabaseErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const e = error as { message?: string; details?: string; hint?: string; code?: string };
  return [e.message, e.details, e.hint].filter(Boolean).join(' — ');
}

async function uploadToBucket(file: File, path: string): Promise<string> {
  const bucket = supabase.storage.from('automobile');
  const { data, error } = await bucket.upload(path, file, {
    upsert: true,
    cacheControl: '120',
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data: pub } = bucket.getPublicUrl(data.path);
  return freshImageUrl(pub.publicUrl) || pub.publicUrl;
}

function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const id = (row.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

function sortAutomobileModels(
  list: AutomobileModelRow[],
  brandMap: Map<string, AutomobileBrandRow>,
): AutomobileModelRow[] {
  if (brandMap.size === 0) return dedupeById(list);

  const brandSortKey = (brandId: string): string => {
    const b = brandMap.get(brandId);
    if (b?.slug) return b.slug;
    if (b?.name) return b.name;
    return `\uffff${brandId}`;
  };

  return dedupeById(list).sort((a, b) => {
    const byBrand = brandSortKey(a.brand_id).localeCompare(brandSortKey(b.brand_id), 'tr', {
      sensitivity: 'base',
    });
    if (byBrand !== 0) return byBrand;
    const byName = a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' });
    if (byName !== 0) return byName;
    const ay = a.year_from ?? 0;
    const by = b.year_from ?? 0;
    if (ay !== by) return by - ay;
    return a.body_type.localeCompare(b.body_type);
  });
}

function normalizeBrandRow(row: Record<string, unknown>): AutomobileBrandRow {
  return {
    ...(row as AutomobileBrandRow),
    is_hidden: row.is_hidden === true,
  };
}

function HiddenSiteBadge({ hidden }: { hidden?: boolean | null }) {
  if (!hidden) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-medium border-amber-300 text-amber-800 bg-amber-50 dark:border-amber-700 dark:text-amber-200 dark:bg-amber-950/40">
      Sitede gizli
    </span>
  );
}

function normalizeModelRow(row: Record<string, unknown>): AutomobileModelRow {
  const brandId = String(row.brand_id ?? row.brandId ?? '').trim();
  return {
    id: String(row.id ?? ''),
    brand_id: brandId,
    model_key: (row.model_key as string | null) ?? null,
    name: String(row.name ?? row.display_name ?? '').trim(),
    display_name: (row.display_name as string | null) ?? null,
    generation_code: (row.generation_code as string | null) ?? null,
    variant_label: (row.variant_label as string | null) ?? null,
    year_from: typeof row.year_from === 'number' ? row.year_from : null,
    year_from_month: typeof row.year_from_month === 'number' ? row.year_from_month : null,
    year_to: typeof row.year_to === 'number' ? row.year_to : null,
    body_type: (row.body_type as AutomobileBodyType) || 'sedan',
    image_url: (row.image_url as string | null) ?? null,
    image_generic:
      typeof row.image_generic === 'boolean' ? row.image_generic : null,
    image_focal_x: typeof row.image_focal_x === 'number' ? row.image_focal_x : null,
    image_focal_y: typeof row.image_focal_y === 'number' ? row.image_focal_y : null,
    image_zoom: typeof row.image_zoom === 'number' ? row.image_zoom : null,
    production_years: Array.isArray(row.production_years) ? (row.production_years as number[]) : null,
    is_hidden: row.is_hidden === true,
    created_at: String(row.created_at ?? ''),
    updated_at: row.updated_at ? String(row.updated_at) : undefined,
  };
}

function applyBrandsAndModels(
  setBrands: React.Dispatch<React.SetStateAction<AutomobileBrandRow[]>>,
  setModels: React.Dispatch<React.SetStateAction<AutomobileModelRow[]>>,
  nextBrands: AutomobileBrandRow[],
  nextModels: AutomobileModelRow[],
) {
  const brandMap = new Map(nextBrands.map((b) => [b.id, b]));
  setBrands(nextBrands);
  setModels(sortAutomobileModels(nextModels, brandMap));
}

const AutomobileManager: React.FC = () => {
  const { user } = useAuth();
  const admin = isSiteAdmin(user);

  const [tab, setTab] = useState<Tab>('models');
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataSource>('catalog');

  const [brands, setBrands] = useState<AutomobileBrandRow[]>([]);
  const [models, setModels] = useState<AutomobileModelRow[]>([]);
  const [siteModelCount, setSiteModelCount] = useState(0);
  const expectedCatalogModelCount = useMemo(() => getAutomobileCatalogBase().modelCount, []);
  const catalogNeedsSeed =
    dataSource === 'supabase' &&
    expectedCatalogModelCount > 0 &&
    siteModelCount < expectedCatalogModelCount * 0.95;
  const duplicateDbRows =
    dataSource === 'supabase' && models.length > siteModelCount ? models.length - siteModelCount : 0;

  const [brandQ, setBrandQ] = useState('');
  const [modelQ, setModelQ] = useState('');
  const [brandQDebounced, setBrandQDebounced] = useState('');
  const [modelQDebounced, setModelQDebounced] = useState('');
  const [modelPage, setModelPage] = useState(1);
  const [modelPageSize, setModelPageSize] = useState<number>(DEFAULT_MODEL_PAGE_SIZE);
  const [brandPage, setBrandPage] = useState(1);
  const [brandPageSize, setBrandPageSize] = useState<number>(DEFAULT_BRAND_PAGE_SIZE);

  // --- Brand modal ---
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<AutomobileBrandRow | null>(null);
  const [brandForm, setBrandForm] = useState({
    slug: '',
    name: '',
    country_code: '',
    logo_url: '',
    description: '',
    founded: '',
    headquarters: '',
    website: '',
    highlights: '',
    legal_name: '',
    company_status: 'unknown' as AutomobileCompanyStatus,
    parent_company: '',
    successor_brand: '',
    closed_year: '',
    closure_reason: '',
    verification_level: 'unverified' as AutomobileVerificationLevel,
    sources: '',
    is_hidden: false,
  });
  const [brandLogoFile, setBrandLogoFile] = useState<File | null>(null);
  const [brandLogoRawFile, setBrandLogoRawFile] = useState<File | null>(null);
  const [brandFilePreviewUrl, setBrandFilePreviewUrl] = useState<string | null>(null);
  const [brandPreviewFailed, setBrandPreviewFailed] = useState(false);
  const [brandLogoMaxEdge, setBrandLogoMaxEdge] = useState(512);
  const [brandImageProcessing, setBrandImageProcessing] = useState(false);
  const [brandImageDims, setBrandImageDims] = useState<{
    raw: ImageDimensions | null;
    processed: ImageDimensions | null;
  }>({ raw: null, processed: null });
  const [brandLogoFrame, setBrandLogoFrame] = useState<AutomobileModelImageFrame>({
    ...DEFAULT_MODEL_IMAGE_FRAME,
  });
  const brandImageJobRef = useRef(0);

  const brandPreviewSrc = brandFilePreviewUrl || brandForm.logo_url.trim() || null;

  // --- Model modal ---
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<AutomobileModelRow | null>(null);
  const [modelForm, setModelForm] = useState({
    brand_id: '',
    name: '',
    body_type: 'sedan' as AutomobileBodyType,
    generation_code: '',
    image_url: '',
    production_years: '',
    is_hidden: false,
  });
  const [modelImageFile, setModelImageFile] = useState<File | null>(null);
  const [modelImageRawFile, setModelImageRawFile] = useState<File | null>(null);
  const [modelFilePreviewUrl, setModelFilePreviewUrl] = useState<string | null>(null);
  const [modelPreviewFailed, setModelPreviewFailed] = useState(false);
  const [modelImageMaxEdge, setModelImageMaxEdge] = useState(1280);
  const [modelImageQuality, setModelImageQuality] = useState(0.85);
  const [modelImageProcessing, setModelImageProcessing] = useState(false);
  const [modelImageDims, setModelImageDims] = useState<{
    raw: ImageDimensions | null;
    processed: ImageDimensions | null;
  }>({ raw: null, processed: null });
  const [modelImageFrame, setModelImageFrame] = useState<AutomobileModelImageFrame>({
    ...DEFAULT_MODEL_IMAGE_FRAME,
  });
  const modelImageJobRef = useRef(0);

  const modelPreviewSrc = modelFilePreviewUrl || modelForm.image_url.trim() || null;

  const applyBrandLogoFile = async (raw: File | null) => {
    setBrandLogoRawFile(raw);
    if (!raw) {
      setBrandLogoFile(null);
      setBrandImageDims({ raw: null, processed: null });
      return;
    }
    const job = ++brandImageJobRef.current;
    setBrandImageProcessing(true);
    try {
      const rawDims = await getImageFileDimensions(raw);
      const processed = await resizeImageFile(raw, {
        maxWidth: brandLogoMaxEdge,
        maxHeight: brandLogoMaxEdge,
        quality: 0.9,
        skipIfSmallerThanBytes: 0,
        forceReencode: true,
      });
      if (job !== brandImageJobRef.current) return;
      const processedDims = await getImageFileDimensions(processed);
      setBrandLogoFile(processed);
      setBrandImageDims({ raw: rawDims, processed: processedDims });
    } catch {
      if (job !== brandImageJobRef.current) return;
      setBrandLogoFile(raw);
      toast.error('Logo işlenemedi, orijinal dosya kullanılacak.');
    } finally {
      if (job === brandImageJobRef.current) setBrandImageProcessing(false);
    }
  };

  const applyModelImageFile = async (raw: File | null) => {
    setModelImageRawFile(raw);
    if (!raw) {
      setModelImageFile(null);
      setModelImageDims({ raw: null, processed: null });
      return;
    }
    const job = ++modelImageJobRef.current;
    setModelImageProcessing(true);
    try {
      const rawDims = await getImageFileDimensions(raw);
      const maxH = Math.round(modelImageMaxEdge * (10 / 16));
      const processed = await resizeImageFile(raw, {
        maxWidth: modelImageMaxEdge,
        maxHeight: maxH,
        quality: modelImageQuality,
        skipIfSmallerThanBytes: 0,
        forceReencode: true,
      });
      if (job !== modelImageJobRef.current) return;
      const processedDims = await getImageFileDimensions(processed);
      setModelImageFile(processed);
      setModelImageDims({ raw: rawDims, processed: processedDims });
    } catch {
      if (job !== modelImageJobRef.current) return;
      setModelImageFile(raw);
      toast.error('Görsel işlenemedi, orijinal dosya kullanılacak.');
    } finally {
      if (job === modelImageJobRef.current) setModelImageProcessing(false);
    }
  };

  useEffect(() => {
    if (!brandLogoFile) {
      setBrandFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(brandLogoFile);
    setBrandFilePreviewUrl(url);
    setBrandPreviewFailed(false);
    return () => URL.revokeObjectURL(url);
  }, [brandLogoFile]);

  useEffect(() => {
    setBrandPreviewFailed(false);
  }, [brandPreviewSrc]);

  useEffect(() => {
    if (!brandLogoRawFile) return;
    void applyBrandLogoFile(brandLogoRawFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandLogoMaxEdge]);

  useEffect(() => {
    if (!modelImageFile) {
      setModelFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(modelImageFile);
    setModelFilePreviewUrl(url);
    setModelPreviewFailed(false);
    return () => URL.revokeObjectURL(url);
  }, [modelImageFile]);

  useEffect(() => {
    setModelPreviewFailed(false);
  }, [modelPreviewSrc]);

  useEffect(() => {
    if (!modelImageRawFile) return;
    void applyModelImageFile(modelImageRawFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelImageMaxEdge, modelImageQuality]);

  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  const modelsByBrandId = useMemo(() => {
    const map = new Map<string, AutomobileModelRow[]>();
    for (const m of models) {
      const list = map.get(m.brand_id) || [];
      list.push(m);
      map.set(m.brand_id, list);
    }
    return map;
  }, [models]);

  const normalize = (text: string): string => {
    return (text || '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim();
  };

  const fetchGenerationRef = useRef(0);

  const brandQNorm = useMemo(() => normalize(brandQDebounced), [brandQDebounced]);
  const modelQNorm = useMemo(() => normalize(modelQDebounced), [modelQDebounced]);

  useEffect(() => {
    const t = window.setTimeout(() => setBrandQDebounced(brandQ), 180);
    return () => window.clearTimeout(t);
  }, [brandQ]);

  useEffect(() => {
    const t = window.setTimeout(() => setModelQDebounced(modelQ), 180);
    return () => window.clearTimeout(t);
  }, [modelQ]);

  const brandSearchPending = useMemo(
    () => normalize(brandQ) !== brandQNorm,
    [brandQ, brandQNorm],
  );
  const modelSearchPending = useMemo(
    () => normalize(modelQ) !== modelQNorm,
    [modelQ, modelQNorm],
  );
  const searchPending = tab === 'brands' ? brandSearchPending : modelSearchPending;

  const sortedAllModels = useMemo(() => {
    if (brands.length === 0) return dedupeById(models);
    return sortAutomobileModels(models, brandById);
  }, [models, brands.length, brandById]);

  const filteredBrands = useMemo(() => {
    const s = brandQNorm;
    const base = [...brands].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
    if (!s) return base;
    const scoreBrand = (b: AutomobileBrandRow): number => {
      const name = normalize(b.name);
      const slug = normalize(b.slug);
      if (name === s || slug === s) return 1000;
      if (name.startsWith(s) || slug.startsWith(s)) return 700;
      if (name.includes(s) || slug.includes(s)) return 400;
      return 0;
    };

    return base
      .map((b) => ({ b, score: scoreBrand(b) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || a.b.name.localeCompare(b.b.name, 'tr'))
      .map((x) => x.b);
  }, [brands, brandQNorm]);

  const filteredModels = useMemo(() => {
    const s = modelQNorm;
    if (!s || s.length < 2) return sortedAllModels;
    const scoreModel = (m: AutomobileModelRow): number => {
      const brand = brandById.get(m.brand_id);
      const name = normalize(m.name);
      const bodyType = normalize(m.body_type);
      const bodyLabel = normalize(AUTOMOBILE_BODY_TYPE_LABELS[m.body_type] || '');
      const brandName = normalize(brand?.name || '');
      const brandSlug = normalize(brand?.slug || '');

      // Highest relevance: model name hits
      if (name === s) return 1200;
      if (name.startsWith(s)) return 900;
      if (name.includes(s)) return 650;

      // Brand hits
      if (brandName === s || brandSlug === s) return 600;
      if (brandName.startsWith(s) || brandSlug.startsWith(s)) return 450;
      if (brandName.includes(s) || brandSlug.includes(s)) return 300;

      // Body type hits
      if (bodyType === s || bodyLabel === s) return 250;
      if (bodyType.startsWith(s) || bodyLabel.startsWith(s)) return 180;
      if (bodyType.includes(s) || bodyLabel.includes(s)) return 120;

      return 0;
    };

    return sortedAllModels
      .map((m) => ({ m, score: scoreModel(m) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const aBrand = brandById.get(a.m.brand_id)?.slug || brandById.get(a.m.brand_id)?.name || '';
        const bBrand = brandById.get(b.m.brand_id)?.slug || brandById.get(b.m.brand_id)?.name || '';
        const byBrand = aBrand.localeCompare(bBrand, 'tr', { sensitivity: 'base' });
        if (byBrand !== 0) return byBrand;
        return a.m.name.localeCompare(b.m.name, 'tr', { sensitivity: 'base' });
      })
      .map((x) => x.m);
  }, [sortedAllModels, modelQNorm, brandById]);

  const visibleBrands = brandSearchPending ? [] : filteredBrands;

  const brandPageCount = useMemo(
    () => Math.max(1, Math.ceil(visibleBrands.length / brandPageSize)),
    [visibleBrands.length, brandPageSize],
  );
  const safeBrandPage = Math.min(brandPage, brandPageCount);
  const paginatedBrands = useMemo(() => {
    const start = (safeBrandPage - 1) * brandPageSize;
    return visibleBrands.slice(start, start + brandPageSize);
  }, [visibleBrands, safeBrandPage, brandPageSize]);

  const visibleModels = useMemo(() => {
    if (modelSearchPending) return [];
    return dedupeById(filteredModels);
  }, [modelSearchPending, filteredModels]);

  const modelPageCount = useMemo(
    () => Math.max(1, Math.ceil(visibleModels.length / modelPageSize)),
    [visibleModels.length, modelPageSize],
  );

  const safeModelPage = Math.min(modelPage, modelPageCount);

  const paginatedModels = useMemo(() => {
    const start = (safeModelPage - 1) * modelPageSize;
    return visibleModels.slice(start, start + modelPageSize);
  }, [visibleModels, safeModelPage, modelPageSize]);

  useEffect(() => {
    setModelPage(1);
  }, [modelQDebounced]);

  useEffect(() => {
    setBrandPage(1);
  }, [brandQDebounced]);

  useEffect(() => {
    setModelPage(1);
    setBrandPage(1);
  }, [tab]);

  useEffect(() => {
    if (modelPage > modelPageCount) setModelPage(modelPageCount);
  }, [modelPage, modelPageCount]);

  useEffect(() => {
    if (brandPage > brandPageCount) setBrandPage(brandPageCount);
  }, [brandPage, brandPageCount]);

  const openBrandModels = (brand: AutomobileBrandRow) => {
    setTab('models');
    setModelQ(brand.name);
    setModelPage(1);
  };

  useEffect(() => {
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFromCatalog = () => {
    const rows = loadCatalogAdminRows();
    applyBrandsAndModels(setBrands, setModels, rows.brands, rows.models);
    setSiteModelCount(rows.models.length);
    setDataSource('catalog');
  };

  const fetchAll = async () => {
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    if (!USE_SUPABASE) {
      loadFromCatalog();
      setLoading(false);
      return;
    }

    try {
      const pageSize = 1000;
      const fetchAllModels = async () => {
        const out: Record<string, unknown>[] = [];
        const seen = new Set<string>();
        let from = 0;
        for (;;) {
          const { data, error } = await supabase
            .from('automobile_models')
            .select('*')
            .order('id', { ascending: true })
            .range(from, from + pageSize - 1);
          if (error) throw error;
          const rows = data || [];
          for (const row of rows) {
            const id = String((row as { id?: string }).id ?? '');
            if (!id || seen.has(id)) continue;
            seen.add(id);
            out.push(row as Record<string, unknown>);
          }
          if (rows.length < pageSize) break;
          from += pageSize;
        }
        return out.map(normalizeModelRow);
      };

      const [{ data: brandRows, error: brandError }, modelRows] = await Promise.all([
        supabase.from('automobile_brands').select('*').order('name'),
        fetchAllModels(),
      ]);

      if (generation !== fetchGenerationRef.current) return;
      if (brandError) throw brandError;
      const nextBrands = (brandRows || []).map((r) => normalizeBrandRow(r as Record<string, unknown>));
      applyBrandsAndModels(setBrands, setModels, nextBrands, modelRows);
      const publishedModelCount = buildSiteCatalogModelCount(nextBrands, modelRows);
      setSiteModelCount(publishedModelCount);
      if (publishedModelCount !== modelRows.length) {
        console.info(
          `[otomobil admin] Sitede görünen: ${publishedModelCount}, veritabanı satırı: ${modelRows.length}`,
        );
      }
      setDataSource('supabase');
    } catch (e) {
      console.warn('Supabase kullanılamadı, katalog.json yükleniyor:', e);
      loadFromCatalog();
      toast(
        'Supabase yok — katalog.json modu. Marka/model listesi ve görsel URL düzenleme çalışır.',
        { icon: 'ℹ️' }
      );
    } finally {
      setLoading(false);
    }
  };

  const catalogModeHint = () => {
    toast(
      'Kalıcı ekleme/silme: scripts/automobile-models-by-brand.mjs düzenleyip npm run build:automobile-catalog çalıştırın.',
      { duration: 6000 }
    );
  };

  const downloadOverrides = () => {
    const blob = new Blob([exportOverridesJson()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'automobile-local-overrides.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Yerel görsel ayarları indirildi');
  };

  const uploadOverrides = async (file: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      importOverridesJson(text, 'merge');
      toast.success('Yerel ayarlar yüklendi (görseller / yıllar hemen uygulanır)');
      loadFromCatalog();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Yükleme başarısız');
    }
  };

  // --- Brands CRUD ---
  const resetBrandImageState = () => {
    brandImageJobRef.current += 1;
    setBrandLogoFile(null);
    setBrandLogoRawFile(null);
    setBrandPreviewFailed(false);
    setBrandImageDims({ raw: null, processed: null });
    setBrandLogoFrame({ ...DEFAULT_MODEL_IMAGE_FRAME });
  };

  const resetModelImageState = () => {
    modelImageJobRef.current += 1;
    setModelImageFile(null);
    setModelImageRawFile(null);
    setModelPreviewFailed(false);
    setModelImageDims({ raw: null, processed: null });
    setModelImageFrame({ ...DEFAULT_MODEL_IMAGE_FRAME });
  };

  const openAddBrand = () => {
    setSelectedBrand(null);
    resetBrandImageState();
    setBrandForm({
      slug: '',
      name: '',
      country_code: 'TR',
      logo_url: '',
      description: '',
      founded: '',
      headquarters: '',
      website: '',
      highlights: '',
      legal_name: '',
      company_status: 'unknown',
      parent_company: '',
      successor_brand: '',
      closed_year: '',
      closure_reason: '',
      verification_level: 'unverified',
      sources: '',
      is_hidden: false,
    });
    setBrandModalOpen(true);
  };

  const openEditBrand = (b: AutomobileBrandRow) => {
    setSelectedBrand(b);
    resetBrandImageState();
    setBrandForm({
      slug: b.slug || '',
      name: b.name || '',
      country_code: b.country_code || '',
      logo_url: b.logo_url || '',
      description: b.description || '',
      founded: b.founded ? String(b.founded) : '',
      headquarters: b.headquarters || '',
      website: b.website || '',
      highlights: (b.highlights || []).join(', '),
      legal_name: b.legal_name || '',
      company_status: b.company_status || 'unknown',
      parent_company: b.parent_company || '',
      successor_brand: b.successor_brand || '',
      closed_year: b.closed_year ? String(b.closed_year) : '',
      closure_reason: b.closure_reason || '',
      verification_level: b.verification_level || 'unverified',
      sources: (b.sources || []).map((s) => `${s.title}|${s.url}`).join('\n'),
      is_hidden: b.is_hidden === true,
    });
    setBrandLogoFrame(logoFrameFromRow(b));
    setBrandModalOpen(true);
  };

  const saveBrand = async () => {
    if (loading) return;
    if (!brandForm.slug.trim() || !brandForm.name.trim()) {
      toast.error('Slug ve marka adı zorunlu.');
      return;
    }

    if (dataSource === 'catalog') {
      const slug = brandForm.slug.trim();
      const logoUrl = brandForm.logo_url.trim();
      if (brandLogoFile) {
        toast.error('Katalog modunda dosya yüklenemez. Görsel URL yapıştırın veya Supabase kurun.');
        return;
      }
      if (logoUrl) setLocalBrandLogo(slug, logoUrl);
      toast.success('Logo URL tarayıcıda kaydedildi (sitede hemen görünür)');
      setBrandModalOpen(false);
      loadFromCatalog();
      catalogModeHint();
      return;
    }

    setLoading(true);
    try {
      let logoUrl = brandForm.logo_url.trim() || null;
      if (brandLogoFile) {
        const ext = (brandLogoFile.name.split('.').pop() || 'png').toLowerCase();
        const p = `brands/${brandForm.slug.trim()}/logo.${ext}`;
        logoUrl = await uploadToBucket(brandLogoFile, p);
      } else if (logoUrl && logoUrl !== (selectedBrand?.logo_url || '').trim()) {
        logoUrl = freshImageUrl(logoUrl);
      }

      const payload = {
        slug: brandForm.slug.trim(),
        name: brandForm.name.trim(),
        country_code: brandForm.country_code.trim() || null,
        logo_url: logoUrl,
        ...logoFrameToPayload(brandLogoFrame),
        description: brandForm.description.trim() || null,
        founded: brandForm.founded ? Number(brandForm.founded) : null,
        headquarters: brandForm.headquarters.trim() || null,
        website: brandForm.website.trim() || null,
        highlights: brandForm.highlights
          ? brandForm.highlights
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        legal_name: brandForm.legal_name.trim() || null,
        company_status: brandForm.company_status,
        parent_company: brandForm.parent_company.trim() || null,
        successor_brand: brandForm.successor_brand.trim() || null,
        closed_year: brandForm.closed_year ? Number(brandForm.closed_year) : null,
        closure_reason: brandForm.closure_reason.trim() || null,
        verification_level: brandForm.verification_level,
        sources: brandForm.sources
          ? brandForm.sources
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line) => {
                const [title, url] = line.split('|');
                return { title: (title || url || '').trim(), url: (url || title || '').trim() };
              })
              .filter((s) => s.url)
          : [],
        is_hidden: brandForm.is_hidden,
      };

      const q = selectedBrand
        ? supabase.from('automobile_brands').update(payload).eq('id', selectedBrand.id).select('*')
        : supabase.from('automobile_brands').insert(payload).select('*');

      const { data, error } = await q;
      if (error) throw error;

      toast.success(selectedBrand ? 'Marka güncellendi' : 'Marka eklendi');
      setBrandModalOpen(false);

      // Refresh local state
      if (data?.length) {
        const row = normalizeBrandRow(data[0] as Record<string, unknown>);
        setBrands((prev) => {
          const next = prev.filter((x) => x.id !== row.id);
          next.push(row);
          next.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
          return next;
        });
      } else {
        await fetchAll();
      }
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error('Error saving brand:', e);
      toast.error(`Kaydedilemedi. ${supabaseErrorMessage(e) || ''}`.trim());
      if (!admin) {
        toast.error('Bu işlem için admin yetkisi (RLS) gerekir. forumadmin ile giriş yapın.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleBrandHidden = async (b: AutomobileBrandRow) => {
    if (dataSource === 'catalog') {
      catalogModeHint();
      return;
    }
    setLoading(true);
    try {
      const is_hidden = !b.is_hidden;
      const { data, error } = await supabase
        .from('automobile_brands')
        .update({ is_hidden })
        .eq('id', b.id)
        .select('*')
        .single();
      if (error) throw error;
      const row = normalizeBrandRow(data as Record<string, unknown>);
      const nextBrands = brands.map((x) => (x.id === row.id ? row : x));
      setBrands(nextBrands);
      setSiteModelCount(buildSiteCatalogModelCount(nextBrands, models));
      toast.success(is_hidden ? 'Marka sitede gizlendi' : 'Marka sitede gösteriliyor');
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error(e);
      toast.error(`Güncellenemedi. ${supabaseErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleModelHidden = async (m: AutomobileModelRow) => {
    if (dataSource === 'catalog') {
      catalogModeHint();
      return;
    }
    setLoading(true);
    try {
      const is_hidden = !m.is_hidden;
      const { data, error } = await supabase
        .from('automobile_models')
        .update({ is_hidden })
        .eq('id', m.id)
        .select('*')
        .single();
      if (error) throw error;
      const row = normalizeModelRow(data as Record<string, unknown>);
      setModels((prev) => {
        const next = prev.map((x) => (x.id === row.id ? row : x));
        setSiteModelCount(buildSiteCatalogModelCount(brands, next));
        return sortAutomobileModels(next, brandById);
      });
      toast.success(is_hidden ? 'Model sitede gizlendi' : 'Model sitede gösteriliyor');
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error(e);
      toast.error(`Güncellenemedi. ${supabaseErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteBrand = async (b: AutomobileBrandRow) => {
    if (!window.confirm(`"${b.name}" markası silinsin mi? (Modelleri de silinir)`)) return;
    if (dataSource === 'catalog') {
      catalogModeHint();
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('automobile_brands').delete().eq('id', b.id);
      if (error) throw error;
      toast.success('Marka silindi');
      setBrands((prev) => prev.filter((x) => x.id !== b.id));
      setModels((prev) => {
        const next = prev.filter((m) => m.brand_id !== b.id);
        setSiteModelCount(buildSiteCatalogModelCount(
          brands.filter((x) => x.id !== b.id),
          next,
        ));
        return next;
      });
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error('Error deleting brand:', e);
      toast.error(`Silinemedi. ${supabaseErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  // --- Models CRUD ---
  const openAddModel = () => {
    setSelectedModel(null);
    resetModelImageState();
    setModelForm({
      brand_id: brands[0]?.id || '',
      name: '',
      body_type: 'sedan',
      generation_code: '',
      image_url: '',
      production_years: '',
      is_hidden: false,
    });
    setModelModalOpen(true);
  };

  const openEditModel = (m: AutomobileModelRow) => {
    const brand = brandById.get(m.brand_id);
    const slug = brand?.slug || '';
    const years = slug ? getLocalModelYears(slug, m.name) : null;
    setSelectedModel(m);
    resetModelImageState();
    setModelForm({
      brand_id: m.brand_id,
      name: m.name,
      body_type: m.body_type,
      generation_code: m.generation_code || '',
      image_url: m.image_url || '',
      production_years: years?.length
        ? formatProductionYearsForInput(years)
        : formatProductionYearsForInput(m.production_years, m.year_from, m.year_to),
      is_hidden: m.is_hidden === true,
    });
    setModelImageFrame(modelImageFrameFromRow(m));
    setModelModalOpen(true);
  };

  const saveModel = async () => {
    if (loading) return;
    if (!modelForm.brand_id || !modelForm.name.trim()) {
      toast.error('Marka ve model adı zorunlu.');
      return;
    }

    if (dataSource === 'catalog') {
      const brand = brandById.get(modelForm.brand_id);
      if (!brand) return;
      if (modelImageFile) {
        toast.error('Katalog modunda dosya yüklenemez. Wikimedia URL yapıştırın.');
        return;
      }
      const imageUrl = modelForm.image_url.trim();
      const label = modelForm.name.trim();
      setLocalModelImage(brand.slug, label, imageUrl);

      const years = parseProductionYearsInput(modelForm.production_years);
      setLocalModelYears(brand.slug, label, years);
      toast.success('Görsel URL kaydedildi — /otomobil sayfasında hemen görünür');
      setModelModalOpen(false);
      loadFromCatalog();
      return;
    }

    setLoading(true);
    try {
      let imageUrl = modelForm.image_url.trim() || null;
      if (modelImageFile) {
        const brand = brandById.get(modelForm.brand_id);
        const slug = brand?.slug || modelForm.brand_id;
        const safeName = modelForm.name.trim().toLowerCase().replace(/[^a-z0-9]+/gi, '-');
        const ext = (modelImageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const p = `models/${slug}/${safeName}.${ext}`;
        imageUrl = await uploadToBucket(modelImageFile, p);
      } else if (imageUrl && imageUrl !== (selectedModel?.image_url || '').trim()) {
        imageUrl = freshImageUrl(imageUrl);
      }

      const brand = brandById.get(modelForm.brand_id);
      const brandSlug = brand?.slug || modelForm.brand_id;
      const modelName = normalizeAutomobileModelDisplayName(brandSlug, modelForm.name.trim());
      const productionYears = parseProductionYearsInput(modelForm.production_years);
      const generationCode = modelForm.generation_code.trim();
      const { yearFrom, yearTo } = productionYearBounds(productionYears);

      const sameNameBody = models.filter(
        (m) =>
          m.brand_id === modelForm.brand_id &&
          m.name === modelName &&
          m.body_type === modelForm.body_type &&
          m.id !== selectedModel?.id,
      );
      if (sameNameBody.length > 0 && yearFrom == null && yearTo == null && !generationCode) {
        toast.error(
          'Aynı model adı ve gövde tipi zaten var. Farklı nesil için üretim yılı (ör. 1985-1990) veya nesil kodu girin.',
        );
        setLoading(false);
        return;
      }

      const modelKey = getAutomobileModelStableKey(brandSlug, {
        name: modelName,
        generationCode: generationCode || undefined,
        variantLabel: selectedModel?.variant_label || undefined,
        yearFrom: yearFrom ?? undefined,
        yearTo: yearTo ?? undefined,
        bodyType: modelForm.body_type,
        imageUrl: '',
      });

      const duplicateKey = models.find(
        (m) =>
          m.brand_id === modelForm.brand_id &&
          m.id !== selectedModel?.id &&
          (m.model_key === modelKey ||
            getAutomobileModelStableKey(brandSlug, {
              name: m.name,
              generationCode: m.generation_code || undefined,
              variantLabel: m.variant_label || undefined,
              yearFrom: m.year_from ?? undefined,
              yearTo: m.year_to ?? undefined,
              bodyType: m.body_type,
              imageUrl: '',
            }) === modelKey),
      );
      if (duplicateKey) {
        toast.error('Bu model varyantı (ad + gövde + yıl/nesil) zaten kayıtlı.');
        setLoading(false);
        return;
      }

      const payload: Record<string, unknown> = {
        brand_id: modelForm.brand_id,
        model_key: modelKey,
        name: modelName,
        body_type: modelForm.body_type,
        generation_code: generationCode || null,
        year_from: yearFrom,
        year_to: yearTo,
        image_url: imageUrl,
        image_generic: imageUrl ? !isUserAutomobileImageUrl(imageUrl) : null,
        production_years: productionYears,
        is_hidden: modelForm.is_hidden,
        ...modelImageFrameToPayload(modelImageFrame),
      };

      let conflictQuery = supabase
        .from('automobile_models')
        .delete()
        .eq('brand_id', modelForm.brand_id)
        .eq('model_key', modelKey);
      if (selectedModel?.id) conflictQuery = conflictQuery.neq('id', selectedModel.id);
      const { error: conflictErr } = await conflictQuery;
      if (conflictErr) throw conflictErr;

      const q = selectedModel
        ? supabase.from('automobile_models').update(payload).eq('id', selectedModel.id).select('*')
        : supabase.from('automobile_models').insert(payload).select('*');

      const { data, error } = await q;
      if (error) {
        if (error.code === '23505') {
          throw new Error(
            'Bu kayıt zaten var (aynı ad + gövde + yıl). Üretim yılını değiştirin veya eski kısıt için Supabase’de 20250528020000_automobile_model_key.sql migration’ını çalıştırın.',
          );
        }
        throw error;
      }

      toast.success(selectedModel ? 'Model güncellendi' : 'Model eklendi');
      setModelModalOpen(false);

      if (data?.length) {
        const row = normalizeModelRow(data[0] as Record<string, unknown>);
        setModels((prev) => {
          const next = prev.filter((x) => x.id !== row.id);
          next.push(row);
          const sorted = sortAutomobileModels(next, brandById);
          setSiteModelCount(buildSiteCatalogModelCount(brands, sorted));
          return sorted;
        });
      } else {
        await fetchAll();
      }
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error('Error saving model:', e);
      toast.error(`Kaydedilemedi. ${supabaseErrorMessage(e) || ''}`.trim());
      if (!admin) {
        toast.error('Bu işlem için admin yetkisi (RLS) gerekir. forumadmin ile giriş yapın.');
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteModel = async (m: AutomobileModelRow) => {
    const brand = brandById.get(m.brand_id);
    if (!window.confirm(`"${brand?.name || 'Marka'} ${m.name}" modeli silinsin mi?`)) return;
    if (dataSource === 'catalog') {
      catalogModeHint();
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from('automobile_models').delete().eq('id', m.id);
      if (error) throw error;
      toast.success('Model silindi');
      setModels((prev) => {
        const next = prev.filter((x) => x.id !== m.id);
        setSiteModelCount(buildSiteCatalogModelCount(brands, next));
        return next;
      });
      void syncPublicAutomobileCatalog();
    } catch (e) {
      console.error('Error deleting model:', e);
      toast.error(`Silinemedi. ${supabaseErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const panel = 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl';
  const title = 'text-gray-900 dark:text-white';
  const muted = 'text-gray-600 dark:text-gray-300';
  const input =
    'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm';
  const btn =
    'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60';
  const btnGhost =
    'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700';

  return (
    <div className="space-y-4">
      <div className={`${panel} p-4`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h1 className={`text-xl font-semibold ${title}`}>Otomobil Yönetimi</h1>
            <p className={`text-sm mt-1 ${muted}`}>
              {dataSource === 'catalog' ? (
                <>
                  <strong>Yedek mod:</strong> catalog.json — veritabanına bağlanılamadı
                </>
              ) : (
                <strong>Canlı veritabanı (Supabase)</strong>
              )}
              {' · '}
              {brands.length} marka
              {dataSource === 'supabase' ? (
                <>
                  {' · '}
                  <strong>Sitede {siteModelCount} model</strong>
                  {duplicateDbRows > 0 && (
                    <span className="text-amber-700 dark:text-amber-300">
                      {' '}
                      · veritabanında {models.length} satır ({duplicateDbRows} yinelenen)
                    </span>
                  )}
                </>
              ) : (
                <> · {models.length} model</>
              )}
              {tab === 'models' && visibleModels.length > 0 && (
                <span className="text-gray-500">
                  {' '}
                  · sayfada {Math.min(modelPageSize, visibleModels.length)} /{' '}
                  {visibleModels.length.toLocaleString('tr-TR')}
                </span>
              )}
              {tab === 'brands' && visibleBrands.length > 0 && (
                <span className="text-gray-500">
                  {' '}
                  · sayfada {Math.min(brandPageSize, visibleBrands.length)} /{' '}
                  {visibleBrands.length.toLocaleString('tr-TR')}
                </span>
              )}
            </p>
            {catalogNeedsSeed && (
              <p className="text-xs mt-2 text-amber-800 dark:text-amber-200">
                Sitede {siteModelCount} model görünüyor; güncel katalog dosyasında{' '}
                {expectedCatalogModelCount} model var. Senkron için:{' '}
                <code className="text-[11px]">npm run seed:automobile-supabase</code> ardından{' '}
                <code className="text-[11px]">npm run dedupe:automobile-supabase</code>
              </p>
            )}
            {duplicateDbRows > 0 && !catalogNeedsSeed && (
              <p className="text-xs mt-2 text-amber-800 dark:text-amber-200">
                Site zaten yinelenen satırları birleştirerek {siteModelCount} model gösteriyor.
                Veritabanını temizlemek için:{' '}
                <code className="text-[11px]">npm run dedupe:automobile-supabase</code>
              </p>
            )}
            {dataSource === 'supabase' && (
              <p className="text-xs mt-2 text-emerald-700 dark:text-emerald-300">
                Hem site hem admin aynı Supabase verisini kullanır. Kaydettiğiniz değişiklikler siteye
                otomatik yansır.
              </p>
            )}
            {dataSource === 'catalog' && (
              <div className="mt-2 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-900 dark:text-indigo-200 space-y-1">
                <p>
                  <strong>Katalog modu:</strong> Liste <code>src/data/automobile/catalog.json</code>{' '}
                  dosyasından gelir. Yeni model eklemek için{' '}
                  <code>scripts/automobile-models-by-brand.mjs</code> →{' '}
                  <code>npm run build:automobile-catalog</code> → deploy.
                </p>
                <p>
                  Görsel URL düzenlemesi tarayıcıda saklanır ve sitede anında görünür. Iveco Stralis
                  dahil — aramada <code>iveco</code> yazın veya{' '}
                  <Link to="/otomobil/iveco" className="underline" target="_blank" rel="noreferrer">
                    kataloğu açın
                  </Link>
                  .
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={downloadOverrides}
                    className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-300 hover:underline"
                  >
                    <Download size={14} /> Yerel ayarları indir (yedek)
                  </button>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <span className="text-indigo-600 dark:text-indigo-300 hover:underline">
                      Yerel ayarları yükle
                    </span>
                    <input
                      type="file"
                      accept="application/json"
                      className="hidden"
                      onChange={(e) => void uploadOverrides(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
                <p className="opacity-80">
                  Not: Tarayıcı verisi temizlenirse bu ayarlar silinebilir. Yedek dosyayı saklarsanız tek tıkla geri
                  yüklersiniz.
                </p>
              </div>
            )}
            {dataSource === 'supabase' && !admin && (
              <p className="text-xs mt-2 text-amber-600 dark:text-amber-400">
                Kaydetme için forumadmin ile giriş ve Supabase RLS gerekir.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setTab('brands')}
              className={tab === 'brands' ? btn : btnGhost}
            >
              Markalar ({brands.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('models')}
              className={tab === 'models' ? btn : btnGhost}
            >
              Modeller ({models.length})
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-2">
          <input
            className={input}
            placeholder={
              tab === 'models'
                ? 'Model ara: marka adı / slug / model adı…'
                : 'Marka ara: marka adı / slug…'
            }
            value={tab === 'brands' ? brandQ : modelQ}
            onChange={(e) => {
              if (tab === 'brands') setBrandQ(e.target.value);
              else setModelQ(e.target.value);
            }}
          />
          {searchPending && (
            <span className="text-xs text-gray-500 dark:text-gray-400 px-1">Filtreleniyor…</span>
          )}
          {!searchPending && (tab === 'brands' ? !brandQNorm : !modelQNorm) && (
            <span className="text-xs text-gray-500 dark:text-gray-400 px-1">
              {tab === 'models'
                ? `Sıralama: Marka A→Z${sortedAllModels[0] ? ` · ilk: ${brandById.get(sortedAllModels[0].brand_id)?.name || '?'}` : ''}`
                : 'Sıralama: A→Z'}
            </span>
          )}
          {tab === 'models' && modelQNorm.length === 1 && (
            <span className="text-xs text-amber-600 dark:text-amber-400 px-1">
              Arama için en az 2 karakter
            </span>
          )}
          <div className="flex gap-2">
            {tab === 'brands' ? (
              <button type="button" className={btn} onClick={openAddBrand} disabled={loading}>
                <Plus size={16} /> Marka Ekle
              </button>
            ) : (
              <button type="button" className={btn} onClick={openAddModel} disabled={loading}>
                <Plus size={16} /> Model Ekle
              </button>
            )}
          </div>
        </div>
      </div>

      {tab === 'brands' ? (
        <div className={`${panel} overflow-hidden`}>
          <AdminListPagination
            page={safeBrandPage}
            pageCount={brandPageCount}
            pageSize={brandPageSize}
            pageSizes={BRAND_PAGE_SIZES}
            total={visibleBrands.length}
            itemLabel="marka"
            onPageChange={setBrandPage}
            onPageSizeChange={(size) => {
              setBrandPageSize(size);
              setBrandPage(1);
            }}
            inputClass={input}
            btnGhostClass={btnGhost}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr className="text-left text-gray-600 dark:text-gray-300">
                  <th className="px-4 py-3 w-24">Logo</th>
                  <th className="px-4 py-3 w-56 min-w-[14rem]">Gövde</th>
                  <th className="px-4 py-3">Model isimleri</th>
                  <th className="px-4 py-3 w-32">url (slug)</th>
                  <th className="px-4 py-3 w-20">Ülke</th>
                  <th className="px-4 py-3 min-w-[8rem]">Ana grup</th>
                  <th className="px-4 py-3 w-28">Düzenle</th>
                  <th className="px-4 py-3 w-20">Sil</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBrands.map((b) => {
                  const brandModels = modelsByBrandId.get(b.id) || [];
                  const bodyCounts = brandModels.reduce(
                    (acc, m) => {
                      acc[m.body_type] = (acc[m.body_type] || 0) + 1;
                      return acc;
                    },
                    {} as Partial<Record<AutomobileBodyType, number>>,
                  );

                  return (
                    <tr
                      key={b.id}
                      className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-700/20"
                    >
                      <td className="px-4 py-3 align-top">
                        {b.logo_url ? (
                          <div className="w-16 h-16 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden p-1">
                            <AutomobileBrandLogoImage
                              src={b.logo_url}
                              frame={logoFrameFromRow(b)}
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-md bg-gray-100 dark:bg-gray-900 flex items-center justify-center border border-gray-200 dark:border-gray-700">
                            <ImageIcon size={18} className="text-gray-400" />
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap gap-1.5">
                          {AUTOMOBILE_BODY_TYPE_ORDER.filter((t) => bodyCounts[t]).map((t) => (
                            <span
                              key={t}
                              className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${
                                AUTOMOBILE_BODY_CHIP[t]
                              }`}
                              title={`${AUTOMOBILE_BODY_TYPE_LABELS[t]}: ${bodyCounts[t]}`}
                            >
                              {AUTOMOBILE_BODY_TYPE_LABELS[t]} ({bodyCounts[t]})
                            </span>
                          ))}
                          {brandModels.length === 0 && (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium text-gray-900 dark:text-white">{b.name}</div>
                          <HiddenSiteBadge hidden={b.is_hidden} />
                        </div>
                        {brandModels.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => openBrandModels(b)}
                            className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            {brandModels.length.toLocaleString('tr-TR')} model — Modeller sekmesinde göster
                          </button>
                        ) : (
                          <div className="mt-1 text-xs text-gray-500">Model: —</div>
                        )}
                      </td>

                      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200 font-mono text-xs">
                        {b.slug}
                      </td>

                      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200">
                        {formatAutomobileCountry(b.country_code) || '—'}
                      </td>

                      <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300 text-xs">
                        {b.parent_company || '—'}
                      </td>

                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            className={btnGhost}
                            onClick={() => openEditBrand(b)}
                            disabled={loading}
                          >
                            <Edit2 size={16} /> Düzenle
                          </button>
                          <button
                            type="button"
                            className={btnGhost}
                            onClick={() => void toggleBrandHidden(b)}
                            disabled={loading || dataSource === 'catalog'}
                            title={b.is_hidden ? 'Sitede göster' : 'Sitede gizle'}
                          >
                            {b.is_hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                            {b.is_hidden ? 'Göster' : 'Gizle'}
                          </button>
                        </div>
                      </td>

                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                          onClick={() => deleteBrand(b)}
                          disabled={loading}
                        >
                          <Trash2 size={16} /> Sil
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!brandSearchPending && visibleBrands.length === 0 && (
                  <tr>
                    <td className="px-4 py-8 text-center text-gray-500" colSpan={8}>
                      Marka bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AdminListPagination
            page={safeBrandPage}
            pageCount={brandPageCount}
            pageSize={brandPageSize}
            pageSizes={BRAND_PAGE_SIZES}
            total={visibleBrands.length}
            itemLabel="marka"
            onPageChange={setBrandPage}
            onPageSizeChange={(size) => {
              setBrandPageSize(size);
              setBrandPage(1);
            }}
            inputClass={input}
            btnGhostClass={btnGhost}
          />
        </div>
      ) : (
        <div className={`${panel} overflow-hidden`}>
          <AdminListPagination
            page={safeModelPage}
            pageCount={modelPageCount}
            pageSize={modelPageSize}
            pageSizes={MODEL_PAGE_SIZES}
            total={visibleModels.length}
            itemLabel="model"
            onPageChange={setModelPage}
            onPageSizeChange={(size) => {
              setModelPageSize(size);
              setModelPage(1);
            }}
            inputClass={input}
            btnGhostClass={btnGhost}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr className="text-left text-gray-600 dark:text-gray-300">
                  <th className="px-4 py-3 w-28">Foto</th>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3 w-44">Marka</th>
                  <th className="px-4 py-3 w-28">Menşei (ülke)</th>
                  <th className="px-4 py-3 w-28">Gövde</th>
                  <th className="px-4 py-3 w-32">Anahtar</th>
                  <th className="px-4 py-3 w-28">Düzenle</th>
                  <th className="px-4 py-3 w-20">Sil</th>
                </tr>
              </thead>
              <tbody>
                {paginatedModels.map((m) => {
                  const b = brandById.get(m.brand_id);
                  const years = (m.production_years || [])
                    .filter((y) => Number.isFinite(y))
                    .sort((a, b2) => a - b2);
                  const title = (m.name || '—').trim();
                  const keyLabel = (m.id || '').trim();

                  return (
                    <tr
                      key={m.id}
                      className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-700/20"
                    >
                      <td className="px-4 py-3 align-top">
                        {m.image_url ? (
                          <div className="relative w-20 h-16 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shrink-0 overflow-hidden">
                            <AutomobileModelImage
                              src={m.image_url}
                              alt={`${b?.name || ''} ${m.name}`}
                              frame={modelImageFrameFromRow(m)}
                            />
                          </div>
                        ) : (
                          <div className="w-20 h-16 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shrink-0 flex items-center justify-center">
                            <ImageIcon size={28} className="text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium text-gray-900 dark:text-white">{title}</div>
                          <HiddenSiteBadge hidden={m.is_hidden} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${
                              AUTOMOBILE_BODY_CHIP[m.body_type]
                            }`}
                            title={m.body_type}
                          >
                            {AUTOMOBILE_BODY_TYPE_LABELS[m.body_type]}
                          </span>
                          {years.slice(0, 14).map((y) => (
                            <span
                              key={y}
                              className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-200"
                            >
                              {y}
                            </span>
                          ))}
                          {years.length > 14 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300">
                              +{years.length - 14}
                            </span>
                          )}
                          {years.length === 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">
                              Yıl: —
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                        {b ? b.name : '—'}
                        <div className="text-xs text-gray-400">{b?.slug}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200">
                        {formatAutomobileCountry(b?.country_code) || '—'}
                        {b?.country_code && (
                          <div className="text-xs text-gray-400 font-mono">{b.country_code}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-medium whitespace-nowrap ${
                            AUTOMOBILE_BODY_CHIP[m.body_type]
                          }`}
                        >
                          {AUTOMOBILE_BODY_TYPE_LABELS[m.body_type]}
                        </span>
                        <div className="text-[10px] text-gray-400 mt-1 font-mono">{m.body_type}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-mono text-xs text-gray-700 dark:text-gray-200 break-all">
                          {keyLabel || '—'}
                        </div>
                        {m.image_url && (
                          <a
                            className="block text-xs text-indigo-500 hover:underline break-all mt-1"
                            href={m.image_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            görsel
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            className={btnGhost}
                            onClick={() => openEditModel(m)}
                            disabled={loading}
                          >
                            <Edit2 size={16} /> Düzenle
                          </button>
                          <button
                            type="button"
                            className={btnGhost}
                            onClick={() => void toggleModelHidden(m)}
                            disabled={loading || dataSource === 'catalog'}
                            title={m.is_hidden ? 'Sitede göster' : 'Sitede gizle'}
                          >
                            {m.is_hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                            {m.is_hidden ? 'Göster' : 'Gizle'}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                          onClick={() => deleteModel(m)}
                          disabled={loading}
                        >
                          <Trash2 size={16} /> Sil
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!modelSearchPending && visibleModels.length === 0 && (
                  <tr>
                    <td className="px-4 py-8 text-center text-gray-500" colSpan={8}>
                      Model bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AdminListPagination
            page={safeModelPage}
            pageCount={modelPageCount}
            pageSize={modelPageSize}
            pageSizes={MODEL_PAGE_SIZES}
            total={visibleModels.length}
            itemLabel="model"
            onPageChange={setModelPage}
            onPageSizeChange={(size) => {
              setModelPageSize(size);
              setModelPage(1);
            }}
            inputClass={input}
            btnGhostClass={btnGhost}
          />
        </div>
      )}

      {/* Brand dialog */}
      <Dialog open={brandModalOpen} onClose={() => setBrandModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
          <Dialog.Panel className="w-full max-w-4xl max-h-[min(92vh,920px)] flex flex-col rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl">
            <form
              className="flex flex-col flex-1 min-h-0"
              onSubmit={(e) => {
                e.preventDefault();
                void saveBrand();
              }}
            >
            <Dialog.Title className={`shrink-0 px-5 pt-5 pb-2 text-lg font-semibold ${title}`}>
              {selectedBrand ? 'Markayı düzenle' : 'Yeni marka'}
            </Dialog.Title>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className={`text-xs ${muted}`}>Slug</label>
                <input
                  className={input}
                  value={brandForm.slug}
                  onChange={(e) => setBrandForm((p) => ({ ...p, slug: e.target.value }))}
                  placeholder="renault"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Marka adı</label>
                <input
                  className={input}
                  value={brandForm.name}
                  onChange={(e) => setBrandForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Renault"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Ülke</label>
                <select
                  className={input}
                  value={brandForm.country_code}
                  onChange={(e) => setBrandForm((p) => ({ ...p, country_code: e.target.value }))}
                >
                  <option value="">— Seçin —</option>
                  {AUTOMOBILE_COUNTRY_OPTIONS.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Kuruluş yılı</label>
                <input
                  className={input}
                  value={brandForm.founded}
                  onChange={(e) => setBrandForm((p) => ({ ...p, founded: e.target.value }))}
                  placeholder="1899"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Logo URL</label>
                <input
                  className={input}
                  value={brandForm.logo_url}
                  onChange={(e) => setBrandForm((p) => ({ ...p, logo_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Logo dosyası (yükle)</label>
                <input
                  className={input}
                  type="file"
                  accept="image/*"
                  onChange={(e) => void applyBrandLogoFile(e.target.files?.[0] || null)}
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className={`text-xs ${muted} flex items-center gap-2`}>
                    Yükleme çözünürlüğü (px)
                    <input
                      type="range"
                      min={128}
                      max={1024}
                      step={64}
                      value={brandLogoMaxEdge}
                      onChange={(e) => setBrandLogoMaxEdge(Number(e.target.value))}
                      className="w-28"
                    />
                    <span className="tabular-nums">{brandLogoMaxEdge}</span>
                  </label>
                  {brandImageProcessing && (
                    <span className="text-xs text-indigo-500">Logo hazırlanıyor…</span>
                  )}
                  {brandLogoRawFile && brandLogoFile && !brandImageProcessing && (
                    <span className={`text-xs ${muted}`}>
                      {formatFileSize(brandLogoRawFile.size)} → {formatFileSize(brandLogoFile.size)}
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-1 ${muted}`}>
                  Storage bucket: <code>automobile</code> → <code>brands/SLUG/logo.ext</code>
                </p>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted} block mb-2`}>Logo kadraj önizlemesi</label>
                <AutomobileLogoFrameEditor
                  src={brandPreviewSrc}
                  alt={`${brandForm.name || 'Marka'} logo`}
                  frame={brandLogoFrame}
                  onChange={setBrandLogoFrame}
                  previewFailed={brandPreviewFailed}
                  onPreviewError={() => setBrandPreviewFailed(true)}
                  mutedClass={muted}
                />
                {brandImageDims.processed && (
                  <p className={`text-xs mt-2 ${muted}`}>
                    Piksel:{' '}
                    {brandImageDims.raw
                      ? `${brandImageDims.raw.width}×${brandImageDims.raw.height} → `
                      : ''}
                    {brandImageDims.processed.width}×{brandImageDims.processed.height}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Açıklama</label>
                <textarea
                  className={`${input} min-h-[88px]`}
                  value={brandForm.description}
                  onChange={(e) => setBrandForm((p) => ({ ...p, description: e.target.value }))}
                  onKeyDown={(e) => textareaSaveKeyDown(e, saveBrand)}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-2">
                <label className={`text-xs ${muted}`}>Merkez</label>
                <input
                  className={input}
                  value={brandForm.headquarters}
                  onChange={(e) => setBrandForm((p) => ({ ...p, headquarters: e.target.value }))}
                  placeholder="İstanbul, Türkiye"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className={`text-xs ${muted}`}>Website</label>
                <input
                  className={input}
                  value={brandForm.website}
                  onChange={(e) => setBrandForm((p) => ({ ...p, website: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Öne çıkanlar (virgülle)</label>
                <input
                  className={input}
                  value={brandForm.highlights}
                  onChange={(e) => setBrandForm((p) => ({ ...p, highlights: e.target.value }))}
                  placeholder="Elektrikli, SUV, Yerli üretim"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Resmi / yasal şirket adı</label>
                <input
                  className={input}
                  value={brandForm.legal_name}
                  onChange={(e) => setBrandForm((p) => ({ ...p, legal_name: e.target.value }))}
                  placeholder="Ford Motor Company"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Şirket durumu</label>
                <select
                  className={input}
                  value={brandForm.company_status}
                  onChange={(e) =>
                    setBrandForm((p) => ({
                      ...p,
                      company_status: e.target.value as AutomobileCompanyStatus,
                    }))
                  }
                >
                  {Object.entries(AUTOMOBILE_COMPANY_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Doğrulama seviyesi</label>
                <select
                  className={input}
                  value={brandForm.verification_level}
                  onChange={(e) =>
                    setBrandForm((p) => ({
                      ...p,
                      verification_level: e.target.value as AutomobileVerificationLevel,
                    }))
                  }
                >
                  <option value="verified">Doğrulanmış</option>
                  <option value="partial">Kısmen doğrulanmış</option>
                  <option value="unverified">Henüz doğrulanmadı</option>
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-2">
                <label className={`text-xs ${muted}`}>Ana şirket / grup</label>
                <input
                  className={input}
                  value={brandForm.parent_company}
                  onChange={(e) => setBrandForm((p) => ({ ...p, parent_company: e.target.value }))}
                  placeholder="Stellantis"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Kapanış yılı</label>
                <input
                  className={input}
                  value={brandForm.closed_year}
                  onChange={(e) => setBrandForm((p) => ({ ...p, closed_year: e.target.value }))}
                  placeholder="2010"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Devam eden marka</label>
                <input
                  className={input}
                  value={brandForm.successor_brand}
                  onChange={(e) => setBrandForm((p) => ({ ...p, successor_brand: e.target.value }))}
                  placeholder="Toyota"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Kapanma / birleşme nedeni</label>
                <textarea
                  className={`${input} min-h-[72px]`}
                  value={brandForm.closure_reason}
                  onChange={(e) => setBrandForm((p) => ({ ...p, closure_reason: e.target.value }))}
                  onKeyDown={(e) => textareaSaveKeyDown(e, saveBrand)}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`text-xs ${muted}`}>Kaynaklar (satır başına: Başlık|URL)</label>
                <textarea
                  className={`${input} min-h-[72px] font-mono text-xs`}
                  value={brandForm.sources}
                  onChange={(e) => setBrandForm((p) => ({ ...p, sources: e.target.value }))}
                  onKeyDown={(e) => textareaSaveKeyDown(e, saveBrand)}
                  placeholder={'Ford Motor Company|https://en.wikipedia.org/wiki/Ford_Motor_Company'}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={`flex items-start gap-2 text-sm ${title}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={brandForm.is_hidden}
                    onChange={(e) => setBrandForm((p) => ({ ...p, is_hidden: e.target.checked }))}
                    disabled={dataSource === 'catalog'}
                  />
                  <span>
                    <span className="font-medium">Sitede gizle</span>
                    <span className={`block text-xs mt-0.5 ${muted}`}>
                      Admin panelinde görünür kalır; /otomobil listesinde ve marka sayfasında gösterilmez.
                      Gizli markanın modelleri de sitede görünmez.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            </div>

            <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 rounded-b-2xl">
              <p className={`text-xs ${muted} sm:mr-auto order-2 sm:order-1`}>
                Enter ile kaydet · Uzun metinlerde Ctrl+Enter
              </p>
              <div className="flex justify-end gap-2 order-1 sm:order-2">
              <button type="button" className={btnGhost} onClick={() => setBrandModalOpen(false)}>
                İptal
              </button>
              <button type="submit" className={btn} disabled={loading}>
                <Upload size={16} /> {loading ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
              </div>
            </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Model dialog */}
      <Dialog open={modelModalOpen} onClose={() => setModelModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
          <Dialog.Panel className="w-full max-w-3xl max-h-[min(85vh,720px)] flex flex-col rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl">
            <form
              className="flex flex-col flex-1 min-h-0"
              onSubmit={(e) => {
                e.preventDefault();
                void saveModel();
              }}
            >
            <Dialog.Title className={`shrink-0 px-5 pt-5 pb-2 text-lg font-semibold ${title}`}>
              {selectedModel ? 'Modeli düzenle' : 'Yeni model'}
            </Dialog.Title>

            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={`text-xs ${muted}`}>Marka</label>
                <select
                  className={input}
                  value={modelForm.brand_id}
                  onChange={(e) => setModelForm((p) => ({ ...p, brand_id: e.target.value }))}
                >
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Model adı</label>
                <input
                  className={input}
                  value={modelForm.name}
                  onChange={(e) => setModelForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Symbol"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Gövde tipi</label>
                <select
                  className={input}
                  value={modelForm.body_type}
                  onChange={(e) =>
                    setModelForm((p) => ({ ...p, body_type: e.target.value as AutomobileBodyType }))
                  }
                >
                  {AUTOMOBILE_BODY_TYPE_ORDER.map((t) => (
                    <option key={t} value={t}>
                      {AUTOMOBILE_BODY_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={`text-xs ${muted}`}>Model görsel URL</label>
                <input
                  className={input}
                  value={modelForm.image_url}
                  onChange={(e) => setModelForm((p) => ({ ...p, image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Nesil kodu (isteğe bağlı)</label>
                <input
                  className={input}
                  value={modelForm.generation_code}
                  onChange={(e) => setModelForm((p) => ({ ...p, generation_code: e.target.value }))}
                  placeholder="KA8, E31…"
                />
              </div>
              <div>
                <label className={`text-xs ${muted}`}>Üretim yılları</label>
                <input
                  className={input}
                  value={modelForm.production_years}
                  onChange={(e) => setModelForm((p) => ({ ...p, production_years: e.target.value }))}
                  placeholder="1985-1990"
                />
              </div>
              <div className="sm:col-span-2">
                <p className={`text-xs ${muted}`}>
                  Aynı model adında birden fazla nesil (ör. Legend sedan 1985-1990 ve 1990-1995) eklemek
                  için her kayıtta <strong>farklı üretim yılı aralığı</strong> yazın. Tire ile aralık:{' '}
                  <code>1985-1990</code>. Virgülle tek tek: <code>1960, 1965</code>.
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={`text-xs ${muted}`}>Model görsel dosyası (yükle)</label>
                <input
                  className={input}
                  type="file"
                  accept="image/*"
                  onChange={(e) => void applyModelImageFile(e.target.files?.[0] || null)}
                />
                <div className="mt-2 space-y-2">
                  <label className={`text-xs ${muted} flex flex-wrap items-center gap-2`}>
                    Yükleme çözünürlüğü (px)
                    <input
                      type="range"
                      min={640}
                      max={1920}
                      step={80}
                      value={modelImageMaxEdge}
                      onChange={(e) => setModelImageMaxEdge(Number(e.target.value))}
                      className="w-32"
                    />
                    <span className="tabular-nums">{modelImageMaxEdge}px</span>
                    <span className="text-gray-400">
                      (yükseklik ≈ {Math.round(modelImageMaxEdge * (10 / 16))}px)
                    </span>
                  </label>
                  <label className={`text-xs ${muted} flex flex-wrap items-center gap-2`}>
                    JPEG kalitesi
                    <input
                      type="range"
                      min={60}
                      max={95}
                      value={Math.round(modelImageQuality * 100)}
                      onChange={(e) => setModelImageQuality(Number(e.target.value) / 100)}
                      className="w-28"
                    />
                    <span className="tabular-nums">{Math.round(modelImageQuality * 100)}%</span>
                  </label>
                  {modelImageProcessing && (
                    <p className="text-xs text-indigo-500">Görsel boyutlandırılıyor…</p>
                  )}
                  {modelImageRawFile && modelImageFile && !modelImageProcessing && (
                    <p className={`text-xs ${muted}`}>
                      Orijinal: {formatFileSize(modelImageRawFile.size)} → Yüklenecek:{' '}
                      {formatFileSize(modelImageFile.size)}
                      {modelImageFile.size < modelImageRawFile.size && (
                        <span className="text-green-600 dark:text-green-400">
                          {' '}
                          (
                          {Math.round(
                            (1 - modelImageFile.size / modelImageRawFile.size) * 100,
                          )}
                          % küçültüldü)
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <p className={`text-xs mt-1 ${muted}`}>
                  Storage bucket: <code>automobile</code> → <code>models/BRAND_SLUG/model.jpg</code>
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={`text-xs ${muted} block mb-2`}>Kadraj önizlemesi</label>
                <AutomobileModelImageFrameEditor
                  src={modelPreviewSrc}
                  alt={`${modelForm.name || 'Model'} önizleme`}
                  frame={modelImageFrame}
                  onChange={setModelImageFrame}
                  previewFailed={modelPreviewFailed}
                  onPreviewError={() => setModelPreviewFailed(true)}
                  mutedClass={muted}
                />
                {modelImageDims.processed && (
                  <p className={`text-xs mt-2 ${muted}`}>
                    Piksel:{' '}
                    {modelImageDims.raw
                      ? `${modelImageDims.raw.width}×${modelImageDims.raw.height} → `
                      : ''}
                    {modelImageDims.processed.width}×{modelImageDims.processed.height}
                    {modelImageFile && (
                      <>
                        {' '}
                        · {formatFileSize(modelImageRawFile?.size ?? 0)} →{' '}
                        {formatFileSize(modelImageFile.size)}
                      </>
                    )}
                  </p>
                )}
                {modelPreviewSrc && !modelImageDims.processed && (
                  <p className={`text-xs mt-2 ${muted} break-all`}>
                    {modelImageFile ? `Dosya: ${modelImageFile.name}` : modelPreviewSrc}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className={`flex items-start gap-2 text-sm ${title}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={modelForm.is_hidden}
                    onChange={(e) => setModelForm((p) => ({ ...p, is_hidden: e.target.checked }))}
                    disabled={dataSource === 'catalog'}
                  />
                  <span>
                    <span className="font-medium">Sitede gizle</span>
                    <span className={`block text-xs mt-0.5 ${muted}`}>
                      Admin panelinde görünür kalır; marka sayfasında ve aramalarda listelenmez.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            </div>

            <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 rounded-b-2xl">
              <p className={`text-xs ${muted} sm:mr-auto order-2 sm:order-1`}>
                Enter ile kaydet
              </p>
              <div className="flex justify-end gap-2 order-1 sm:order-2">
              <button type="button" className={btnGhost} onClick={() => setModelModalOpen(false)}>
                İptal
              </button>
              <button type="submit" className={btn} disabled={loading}>
                <Upload size={16} /> {loading ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
              </div>
            </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default AutomobileManager;

